import {
  User,
  UserPresenceFluid,
} from "@bezoned/shared/fluid/office-schema-factory.js";
import type { UserPresence } from "@bezoned/shared/types/api/user/user-presence-get.types.js";
import type { UserAvailability } from "@bezoned/shared/types/user-presence.js";
import { userPresenceGetDefault } from "@bezoned/shared/utils/user-presence.js";

import type { UserInfo } from "../auth/auth.context.js";
import type { FluidSharedTree } from "./fluid.client.js";

export const getUser = ({
  userId,
  sharedTree,
}: {
  userId: string;
  sharedTree: FluidSharedTree;
}) => sharedTree.root.users.find((user) => user?.id === userId) ?? null;

const deduplicateUserIdsInLocation = ({
  length,
  readAt,
  removeAt,
  seen,
}: {
  length: number;
  readAt: (index: number) => string | undefined;
  removeAt: (index: number) => void;
  seen: Set<string>;
}) => {
  for (let i = length - 1; i >= 0; i--) {
    const userId = readAt(i);
    if (!userId || seen.has(userId)) {
      removeAt(i);
      continue;
    }

    seen.add(userId);
  }
};

const hasDuplicateUserIds = (
  userIds: Iterable<string>,
  seen: Set<string>
): boolean => {
  for (const userId of userIds) {
    if (!userId) {
      continue;
    }

    if (seen.has(userId)) {
      return true;
    }

    seen.add(userId);
  }

  return false;
};

const deduplicateEntranceArea = (
  sharedTree: FluidSharedTree,
  seen: Set<string>
) => {
  const entranceUsers = sharedTree.root.entranceArea.users;
  deduplicateUserIdsInLocation({
    length: entranceUsers.length,
    readAt: (index) => entranceUsers[index] as string | undefined,
    removeAt: (index) => entranceUsers.removeAt(index),
    seen,
  });
};

const deduplicateTableParticipants = (
  sharedTree: FluidSharedTree,
  seen: Set<string>
) => {
  for (
    let tableIndex = sharedTree.root.tables.length - 1;
    tableIndex >= 0;
    tableIndex--
  ) {
    const table = sharedTree.root.tables[tableIndex];
    if (!table) {
      continue;
    }

    deduplicateUserIdsInLocation({
      length: table.participants.length,
      readAt: (index) => table.participants[index] as string | undefined,
      removeAt: (index) => table.participants.removeAt(index),
      seen,
    });
  }
};

const deduplicateRoomUsers = (
  sharedTree: FluidSharedTree,
  seen: Set<string>
) => {
  for (
    let roomIndex = sharedTree.root.rooms.length - 1;
    roomIndex >= 0;
    roomIndex--
  ) {
    const room = sharedTree.root.rooms[roomIndex];
    if (!room) {
      continue;
    }

    deduplicateUserIdsInLocation({
      length: room.users.length,
      readAt: (index) => room.users[index] as string | undefined,
      removeAt: (index) => room.users.removeAt(index),
      seen,
    });
  }
};

const deduplicateUsersList = (sharedTree: FluidSharedTree) => {
  const uniqueUsers = new Set<string>();
  for (let i = sharedTree.root.users.length - 1; i >= 0; i--) {
    const user = sharedTree.root.users[i];
    if (!user) {
      continue;
    }

    if (uniqueUsers.has(user.id)) {
      sharedTree.root.users.removeAt(i);
      continue;
    }

    uniqueUsers.add(user.id);
  }
};

export const removeAllDuplicateUsers = (sharedTree: FluidSharedTree) => {
  const seen = new Set<string>();
  deduplicateEntranceArea(sharedTree, seen);
  deduplicateTableParticipants(sharedTree, seen);
  deduplicateRoomUsers(sharedTree, seen);
  deduplicateUsersList(sharedTree);
};

/**
 * Checks whether any userId appears in more than one location
 * (entrance area, tables, rooms) or more than once within a location,
 * or if there are duplicate User objects in the users[] array.
 *
 * This is a read-only check used to decide whether
 * `removeAllDuplicateUsers` needs to run.
 */
export const hasDuplicateUsers = (sharedTree: FluidSharedTree): boolean => {
  const seen = new Set<string>();

  if (hasDuplicateUserIds(sharedTree.root.entranceArea.users, seen)) {
    return true;
  }

  for (const table of sharedTree.root.tables) {
    if (hasDuplicateUserIds(table.participants, seen)) {
      return true;
    }
  }

  for (const room of sharedTree.root.rooms) {
    if (hasDuplicateUserIds(room.users, seen)) {
      return true;
    }
  }

  // Check the users[] array for duplicate User objects
  const userIds = new Set<string>();
  for (const user of sharedTree.root.users) {
    if (userIds.has(user.id)) {
      return true;
    }
    userIds.add(user.id);
  }

  return false;
};

export const isUserInStartZone = ({
  userId,
  sharedTree,
}: {
  userId: string;
  sharedTree: FluidSharedTree;
}) => sharedTree.root.entranceArea.users.includes(userId);

export const getTable = ({
  tableId,
  sharedTree,
}: {
  tableId: string;
  sharedTree: FluidSharedTree;
}) => sharedTree.root.tables.find((table) => table?.id === tableId) ?? null;

export const getRoom = ({
  roomId,
  sharedTree,
}: {
  roomId: string | null;
  sharedTree: FluidSharedTree;
}) =>
  roomId
    ? (sharedTree.root.rooms.find((room) => room.id === roomId) ?? null)
    : null;

const userLeaveAll = ({
  userId,
  sharedTree,
}: {
  userId: string;
  sharedTree: FluidSharedTree;
}) => {
  for (const table of sharedTree.root.tables) {
    table.removeParticipant(userId);
  }

  for (const room of sharedTree.root.rooms) {
    room.removeUser(userId);
  }

  sharedTree.root.entranceArea.removeUser(userId);
};

export function userMoveToEntranceArea({
  userId,
  sharedTree,
}: {
  userId: string;
  sharedTree: FluidSharedTree;
}) {
  removeAllDuplicateUsers(sharedTree);
  userLeaveAll({ sharedTree, userId });
  sharedTree.root.entranceArea.addUser(userId);
}

export const addUserIfNotExist = ({
  user,
  sharedTree,
  forceEntranceArea = true,
}: {
  user: UserInfo;
  sharedTree: FluidSharedTree;
  forceEntranceArea?: boolean;
}) => {
  removeAllDuplicateUsers(sharedTree);
  let fluidUser = getUser({ sharedTree, userId: user.id });
  const isNewUser = !fluidUser;

  if (isNewUser) {
    fluidUser = new User({
      acs_id: "",
      id: user.id,
      isOnline: true,
      name: user.name,
      picture: "",
      presence: new UserPresenceFluid(userPresenceGetDefault()),
    });

    sharedTree.root.users.addUser(fluidUser);
  }

  if (fluidUser) {
    fluidUser.isOnline = true;
  }

  // Move to entrance area only if:
  // 1. This is a new user (never existed in Fluid), OR
  // 2. This is a fresh page load (forceEntranceArea is true)
  if (isNewUser || forceEntranceArea) {
    userMoveToEntranceArea({ sharedTree, userId: user.id });
  }

  return getUser({ sharedTree, userId: user.id });
};

export const getTableIdFromUser = ({
  userId,
  sharedTree,
}: {
  userId: string;
  sharedTree: FluidSharedTree;
}) => {
  for (const table of sharedTree.root.tables) {
    if (table?.participants?.find((item) => item === userId)) {
      return table.id;
    }
  }

  return null;
};

export const isUserAtTable = ({
  tableId,
  userId,
  sharedTree,
}: {
  tableId: string | null;
  userId: string;
  sharedTree: FluidSharedTree;
}) => {
  if (!tableId) {
    return false;
  }

  const table = getTable({ sharedTree, tableId });
  if (!table) {
    return false;
  }

  return table.participants.includes(userId);
};

export const isUserInRoom = ({
  roomId,
  userId,
  sharedTree,
}: {
  roomId: string;
  userId: string;
  sharedTree: FluidSharedTree;
}) => {
  const room = getRoom({ roomId, sharedTree });
  if (!room) {
    return false;
  }

  return room.users.includes(userId);
};

export const isUserInAreaWithoutVoiceCall = ({
  userId,
  sharedTree,
}: {
  userId: string;
  sharedTree: FluidSharedTree;
}) => {
  const isInEntranceArea = isUserInStartZone({ sharedTree, userId });
  const isInAnyRoom = sharedTree.root.rooms.some((room) =>
    room.users.includes(userId)
  );

  return isInEntranceArea || isInAnyRoom;
};

export const updateUserPresenceInFluid = ({
  userId,
  presence,
  sharedTree,
}: {
  userId: string;
  presence: UserPresence;
  sharedTree: FluidSharedTree;
}) => {
  const user = getUser({ sharedTree, userId });

  if (user) {
    user.setPresence(presence);
  }
};

export const updateUserAvailabilityInFluid = ({
  userId,
  availability,
  sharedTree,
}: {
  userId: string;
  availability: UserAvailability;
  sharedTree: FluidSharedTree;
}) => {
  const user = getUser({ sharedTree, userId });

  if (user) {
    user.presence.setAvailability(availability);
  }
};

export const updateUserStatusMessageInFluid = ({
  userId,
  statusMessage,
  sharedTree,
}: {
  userId: string;
  statusMessage: string | null;
  sharedTree: FluidSharedTree;
}) => {
  const user = getUser({ sharedTree, userId });

  if (user) {
    user.presence.setStatusMessage(statusMessage);
  }
};

export const getUserPresenceFromFluid = ({
  userId,
  sharedTree,
}: {
  userId: string;
  sharedTree: FluidSharedTree;
}): UserPresence | null => {
  const user = getUser({ sharedTree, userId });

  if (!user?.presence) {
    return null;
  }

  return {
    availability: user.presence.availability as UserAvailability,
    outOfOffice: {
      isOutOfOffice: user.presence.outOfOffice?.isOutOfOffice ?? false,
      message: user.presence.outOfOffice?.message ?? null,
    },
    statusMessage: user.presence.statusMessage ?? null,
  };
};

export const userMoveToTable = ({
  userId,
  tableId,
  sharedTree,
}: {
  userId: string;
  tableId: string;
  sharedTree: FluidSharedTree;
}) => {
  removeAllDuplicateUsers(sharedTree);
  userLeaveAll({ sharedTree, userId });

  const table = getTable({ sharedTree, tableId });
  if (!table) {
    return;
  }

  table.addParticipant(userId);
};

export const userMoveToRoom = ({
  userId,
  roomId,
  sharedTree,
}: {
  userId: string;
  roomId: string;
  sharedTree: FluidSharedTree;
}) => {
  removeAllDuplicateUsers(sharedTree);
  userLeaveAll({ sharedTree, userId });

  const room = getRoom({ roomId, sharedTree });
  if (!room) {
    return;
  }

  room.addUser(userId);
};

export const updateTableDefaultStatus = ({
  tableId,
  defaultStatus,
  sharedTree,
}: {
  tableId: string;
  defaultStatus: UserAvailability | undefined;
  sharedTree: FluidSharedTree;
}) => {
  const table = getTable({ sharedTree, tableId });
  if (table) {
    table.setDefaultStatus(defaultStatus);
  }
};
